import { Three3DScene } from 'redhotmagma-visualization/Three/Three3DScene';
import { THREE } from 'redhotmagma-visualization/ExposedModules';
import { createPointerEventListener, createPointerHitListener } from '../Utils/pointerEvents';
import getIntersection, { findClosestEditable, findFirstEditable } from './getIntersection';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import {
  getComponent,
  getOption,
  getOptionInputValue,
  getSelectedOptions,
  getFrameHeight,
} from '../Reducers/Configurator/Selectors';
import { setClickToAddDoorHintState } from '../Reducers/App/Actions';

const {
  Group,
  Box3,
  Vector3,
  Fog,
  Color,
  BoxGeometry,
  Mesh,
  MeshBasicMaterial,
  WebGLRenderer,
  ACESFilmicToneMapping,
  Sphere,
} = THREE;

const getFraction = (value) => (value < 1 ? value : value % Math.floor(value));

const getModelBox = (models) => {
  const box = new Box3();
  if (!models) {
    box.min.set(0, 0, 0);
    box.max.set(0, 0, 0);
    return box;
  }
  if (!(models instanceof Array)) {
    models = [models];
  }
  for (const model of models) {
    box.expandByObject(model, true);
  }
  return box;
};

const getModelHeight = (model) => getModelBox(model).getSize(new Vector3()).y;
const getModelLength = (model) => getModelBox(model).getSize(new Vector3()).x;

const getBoxHeight = (box) => box.getSize(new Vector3()).y;
const getBoxWidth = (box) => box.getSize(new Vector3()).x;

const findObjectBy = (root, checkFn) => {
  let result;
  root?.traverse((node) => {
    if (!result && checkFn(node)) {
      result = node;
    }
  });
  return result;
};

const applyWorldMatrix = (obj, ...fixFunctions) => {
  if (!obj) {
    return obj;
  }
  const parent = obj.parent;
  parent?.updateWorldMatrix(true, true);
  parent?.updateMatrixWorld();
  const matrixWrapper = new Group();
  parent && matrixWrapper.applyMatrix4(parent.matrixWorld);
  matrixWrapper.add(obj);
  fixFunctions.forEach((fixFn) => fixFn(matrixWrapper));
  return new Group().add(matrixWrapper);
};

const centerX = (obj) => {
  obj.updateWorldMatrix(true, true);
  obj.position.x -= getModelBox(obj).getCenter(new Vector3()).x;
  return obj;
};

const makeTopOrigin = (obj) => {
  obj.updateWorldMatrix(true, true);
  const box = getModelBox(obj);
  obj.position.y -= box.getCenter(new Vector3()).y + box.getSize(new Vector3()).y * 0.5;
  return obj;
};

const makeBottomOrigin = (obj) => {
  obj.updateWorldMatrix(true, true);
  const box = getModelBox(obj);
  obj.position.y -= box.getCenter(new Vector3()).y - box.getSize(new Vector3()).y * 0.5;
  return obj;
};

function setTemporaryColor(object, color) {
  if (object.userData?.noHighlight) {
    return;
  }
  if (object.material) {
    if (!object.originalColor) {
      object.originalColor = object.material.color;
    }
    object.material.color = color;
  }
  for (const child of object.children) {
    setTemporaryColor(child, color);
  }
}

function restoreOriginalColor(object) {
  if (object.material) {
    object.material.color = object.originalColor;
  }
  for (const child of object.children) {
    restoreOriginalColor(child);
  }
}

const cloneMaterialDeep = (object) => {
  if (object.material) {
    object.material = object.material.clone();
  }
  object.traverse((child) => {
    if (child.material) {
      child.material = child.material.clone();
    }
  });
};

const areCameraViewsEqual = (view1, view2, maxDistance = 0.1) =>
  view1.position.distanceTo(view2.position) <= maxDistance &&
  view1.lookat.distanceTo(view2.lookat) <= maxDistance;

const projectVec3ToScreen = (renderer, camera, vec3) => {
  const rect = renderer.domElement.getBoundingClientRect();
  camera.updateMatrixWorld();
  vec3.project(camera);
  const left = (vec3.x + 1) * rect.width * 0.5 + rect.left;
  const top = -(vec3.y - 1) * rect.height * 0.5 + rect.top;
  vec3.set(left, top, 0);
  return {
    left,
    top,
  };
};

const highlightColor = new Color(0xa5cd41);

export default class Visualization extends Three3DScene {
  defaultCameraViewsPerItem = {
    default: new Vector3(0, 1, 2).normalize(),
    vista_one: new Vector3(0, 0.6, 3).normalize(),
    vista_two: new Vector3(-3, 0.2, 3).normalize(),
  };

  maxDistanceCorrectionPerItem = {
    default: 0,
    vista_one: 0,
    vista_two: 1,
  };

  constructor(cfg) {
    super(cfg);
    Object.defineProperty(this, 'defaultCameraPosition', {
      get() {
        return (
          this.defaultCameraViewsPerItem[this._configuration?.item.identifier] ||
          this.defaultCameraViewsPerItem.default
        );
      },
    });
    Object.defineProperty(this, 'defaultScreenshotPosition', {
      get() {
        return this.defaultCameraPosition;
      },
    });
  }

  createRenderer() {
    const renderer = new WebGLRenderer({
      antialias: true,
    });
    renderer.toneMapping = ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;

    renderer.setPixelRatio(window.devicePixelRatio);

    this.config.useEnvironmentMap && this.loadEnvMap(renderer);

    return renderer;
  }

  fitSceneToCamera() {
    const { controls, camera } = this;
    const box = this.getSceneContainerBox();

    const size = box.getSize(new Vector3());

    const sphere = new Sphere();
    box.getBoundingSphere(sphere);

    if (sphere.radius < 0.001) {
      return;
    }

    const minDistance = Math.min(sphere.radius + 1);

    const fitDistance = (size.y / 2 / Math.tan((Math.PI * this.camera.fov) / 360)) * 3;
    const maxDistance =
      Math.max(minDistance * 2, fitDistance) +
      (this.maxDistanceCorrectionPerItem[this._configuration?.item.identifier] ||
        this.maxDistanceCorrectionPerItem.default);

    // update controls
    if (minDistance === controls.minDistance) {
      return;
    }
    controls.minDistance = minDistance;
    controls.maxDistance = maxDistance;
    camera.near = minDistance * 0.01;
    camera.far = maxDistance * 100;
    // update current position only if it does not fit into the distance range
    const currentDistance = sphere.center.distanceTo(camera.position);
    if (currentDistance < minDistance || currentDistance > maxDistance) {
      camera.position.copy(camera.position.normalize().multiplyScalar(maxDistance * 0.5));
      controls.target = sphere.center;
    }
    camera.updateProjectionMatrix();

    controls.update();
  }

  setupDefaultControls() {
    super.setupDefaultControls();
    this.controls.maxPolarAngle = Math.PI * 0.5;

    const getEditableFromIntersects = (evt, renderer, camera, scene) => {
      const intersects = getIntersection(evt, renderer, camera, scene, (intersects) => intersects);
      if (!intersects) {
        return;
      }
      let object;
      for (const intersect of intersects) {
        object = findClosestEditable(intersect.object);
        if (object) {
          break;
        }
      }
      return object;
    };

    const { renderer, controls, camera, scene } = this;
    this.lastHighlightedObject = null;
    createPointerEventListener(renderer.domElement, 'move', (evt) => {
      if (evt.target !== renderer.domElement || !controls.enabled) {
        return;
      }
      const object = getEditableFromIntersects(evt, renderer, camera, scene);
      if (object !== this.lastHighlightedObject) {
        if (object?.userData.highlightable) {
          this.highlightObject(object);
        } else if (!this.keepHighlighted) {
          this.removeHighlight();
        }
        this.renderFrame();
      }
    });

    createPointerHitListener(renderer.domElement, (evt) => {
      const object = getEditableFromIntersects(evt, renderer, camera, scene);
      object &&
        Services.configurator.handleWallSegmentClick(evt, object.userData.wall, object.userData.position);
    });
  }

  highlightObject(object) {
    this.lastHighlightedObject && restoreOriginalColor(this.lastHighlightedObject);
    this.lastHighlightedObject = object;

    const { renderer, camera } = this;

    setTemporaryColor(object, highlightColor);
    const box = getModelBox(object);
    const aboveObjVec = new Vector3((box.min.x + box.max.x) * 0.5, box.max.y, (box.min.z + box.max.z) * 0.5);
    const { top, left } = projectVec3ToScreen(renderer, camera, aboveObjVec);

    Services.store.dispatch(setClickToAddDoorHintState({ show: true, top, left }));
  }

  removeHighlight() {
    Services.store.dispatch(setClickToAddDoorHintState({ show: false, top: 0, left: 0 }));
    this.lastHighlightedObject && restoreOriginalColor(this.lastHighlightedObject);
    this.lastHighlightedObject = null;
  }

  getSceneContainerBox() {
    const box = new Box3();
    box.expandByObject(this.buildContainer || this.sceneContainer);
    if (Math.abs(box.min.z) > 1) {
      box.min.z = -Math.round(Math.max(Math.abs(box.max.x), Math.abs(box.min.z)));
      box.max.x = Math.round(Math.max(Math.abs(box.max.x), Math.abs(box.min.z)));
    } else {
      box.min.z = Math.round(box.min.z);
      box.max.x = Math.round(box.max.x);
    }
    box.min.x = Math.round(box.min.x);
    box.max.z = Math.round(box.max.z);
    return box;
  }

  createScene() {
    const scene = super.createScene();
    scene.background = new Color(0xffffff);
    scene.fog = new Fog(0xffffff, 12, 28);
    scene.environmentRotation.y = Math.PI;
    return scene;
  }

  fixSceneScaling() {
    this.setupGround();
    super.fixSceneScaling();
  }

  setupGround() {
    // use this function to move environment models out of the sceneContainer
    const envModels = this.getModelsForComponent('basemodel/env_');
    if (!envModels.length) {
      return;
    }
    this.sceneContainer.remove(...envModels);
    if (!this.envContainer || this.envContainer.parent !== this.scene) {
      this.envContainer = new Group();
      this.scene.add(this.envContainer);
    }
    this.envContainer.clear();
    this.envContainer.add(...envModels);
  }

  renderFrame() {
    if (!this.isAnimating()) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = window.requestAnimationFrame(this.render.bind(this));
    }
  }

  getModelsForComponent(pathPart) {
    const filterFn =
      pathPart instanceof RegExp
        ? (obj) => pathPart.test(obj.userData.path)
        : (obj) => obj.userData.path?.includes(pathPart);
    return this.sceneContainer.children.filter(filterFn);
  }

  getClones(pathPart) {
    return this.getModelsForComponent(pathPart).map((obj) => {
      const clone = obj.clone();
      clone.visible = true;
      return clone;
    });
  }

  getReferenceBox() {
    const heightReferenceModel = this.getModelsForComponent('/frame_color/')[0];
    if (!heightReferenceModel) {
      return;
    }
    return getModelBox([heightReferenceModel]);
  }

  getDefaultEnvMapURL() {
    return `${this._modelBasePath}/visualization_component/hdr/envmap.hdr`;
  }

  getFoilModelWithCut(height) {
    const foil = this.getClones('/foil_covering/')[0];
    if (!foil) {
      return;
    }
    const foilBox = getModelBox(foil);
    const foilSize = foilBox.getSize(new Vector3());
    const cutSize = foilBox.max.y - height;
    if (cutSize <= 0) {
      return foil;
    }
    const geometry = new BoxGeometry(foilSize.x, cutSize, foilSize.z + 0.001);
    const cut = new Mesh(geometry, new MeshBasicMaterial({ colorWrite: false, color: 'green' }));
    cut.position.y = foilBox.max.y - cutSize * 0.5;
    cut.position.z = foilBox.getCenter(new Vector3()).z;
    cut.renderOrder = 1;
    foil.renderOrder = 2;
    const foilGroup = new Group();
    foilGroup.add(foil, cut);
    return foilGroup;
  }

  buildSingleWall(wallSegmentPositions, height, wall, isSegmentEditable, isHighlightEnabled) {
    const referenceBox = this.getReferenceBox();
    const referenceSize = referenceBox.getSize(new Vector3());

    const wallModel = this.getClones('basemodel/glasspanel')[0];
    const foilModel = this.getFoilModelWithCut(height);

    const wallBox = getModelBox(wallModel);
    const wallSize = wallBox.getSize(new Vector3());
    const wallOffsetX = wallSize.x * 0.5;
    const segmentHeight = height / referenceSize.y;

    const wallGroup = new Group();

    for (const { length, x, topWallHeight, topWallBottom, door } of wallSegmentPositions) {
      for (let i = 0; i < length; i++) {
        if (door && !topWallHeight) {
          // skip adding glass panel if a full height door is added
          continue;
        }
        const segment = wallModel.clone();
        const position = x + i;
        cloneMaterialDeep(segment);
        if (topWallHeight) {
          const segmentHeight = topWallHeight / referenceSize.y;
          makeBottomOrigin(segment);
          segment.scale.y = segmentHeight;
          segment.position.x += wallOffsetX + position * wallSize.x;
          segment.position.y += topWallBottom;
        } else {
          segment.scale.y = segmentHeight;
          segment.position.x += wallOffsetX + position * wallSize.x;
          segment.position.y -= referenceBox.min.y * segmentHeight;
        }
        const editable = isSegmentEditable({ position, wall });
        const highlightable = editable && isHighlightEnabled({ position, wall });
        Object.assign(segment.userData, {
          editable,
          highlightable,
          position,
          wall,
        });
        wallGroup.add(segment);

        if (!door && foilModel) {
          const foilClone = foilModel.clone();
          foilClone.position.x = segment.position.x;
          foilClone.position.y = segment.position.y;
          wallGroup.add(foilClone);
        }
      }
    }

    return wallGroup;
  }

  getWallSegmentPositions(doors, length, topWallHeight, topWallBottom) {
    if (!doors?.length) {
      return [{ length, x: 0 }];
    }
    const positions = [];
    doors[0].position && positions.push({ length: doors[0].position, x: 0 });

    for (let i = 1; i < doors.length; i++) {
      positions.push({ length: doors[i].position - doors[i - 1].position - 1, x: doors[i - 1].position + 1 });
    }

    const lastIndex = doors.length - 1;
    positions.push({ length: length - doors[lastIndex].position - 1, x: doors[lastIndex].position + 1 });

    for (let i = 0; i < doors.length; i++) {
      positions.push({ length: 1, x: doors[i].position, door: true, topWallHeight, topWallBottom });
    }

    return positions;
  }

  buildFrame(
    height,
    length,
    wallSegmentPositions,
    addSidePanel,
    mirrorSidePanel = false,
    withLeftProfile = false
  ) {
    const frameModels = this.getClones(/\/frame_color\/options\/frame_color_[^/]+\/frame_color_[^/]+$/);
    const frameCornerModels = this.getClones('/corner_formation+frame_color/');
    if (!frameModels?.length) {
      return;
    }
    const profileBox = getModelBox(frameModels);
    const profileSize = profileBox.getSize(new Vector3());

    const scaleY = height / profileSize.y;
    const topPosition = profileBox.max.y * scaleY;

    const bottomProfile = applyWorldMatrix(
      findObjectBy(frameModels[0], (object) => object?.name?.includes('profile_bottom_scaleable')),
      centerX
    );

    const bottomProfileCorner = applyWorldMatrix(
      findObjectBy(frameCornerModels[0], (object) => object?.name?.includes('profile_bottom_scaleable')),
      centerX
    );

    const sidePanel = applyWorldMatrix(
      findObjectBy(frameModels[0], (object) => object?.name?.includes('sidepanel_scaleable'))
    );

    const bottomProfileCornerBox = getModelBox(bottomProfileCorner);
    const depthCorrection = !bottomProfileCorner
      ? 0
      : bottomProfileCornerBox.max.z - (0.5 + bottomProfileCornerBox.min.x);
    const bottomProfileCornerLength = getModelLength(bottomProfileCorner) - depthCorrection;
    if (bottomProfileCorner) {
      bottomProfileCorner.scale.x = -1;
      bottomProfileCorner.position.x = getModelLength(bottomProfileCorner) * 0.5 - depthCorrection;
    }

    const topProfileCorner = bottomProfileCorner?.clone();
    if (topProfileCorner) {
      topProfileCorner.scale.y = -1;
      topProfileCorner.position.y = topPosition;
    }

    const bottomProfileBox = getModelBox([bottomProfile]);
    const bottomProfileSize = bottomProfileBox.getSize(new Vector3());
    const scaledLength = length - bottomProfileCornerLength;
    bottomProfile.scale.x = scaledLength / bottomProfileSize.x;

    const topProfile = bottomProfile.clone();
    topProfile.position.x = (length + bottomProfileCornerLength) * 0.5;
    topProfile.position.y = topPosition;
    topProfile.rotation.z = Math.PI;

    const sidePanels = [];

    const bottomProfileGroup = new Group();
    for (const { length, x, door } of wallSegmentPositions) {
      if (door && addSidePanel) {
        const sidePanelClone = sidePanel.clone();
        sidePanelClone.position.x = x + 0.5;
        sidePanelClone.scale.x = mirrorSidePanel ? -1 : 1;
        sidePanelClone.scale.y = scaleY;
        sidePanels.push(sidePanelClone);
      }
      if (door) {
        continue;
      }
      const bottomProfileClone = bottomProfile.clone();
      bottomProfileClone.scale.x = length / bottomProfileSize.x;
      bottomProfileClone.position.x = x + length * 0.5;
      bottomProfileGroup.add(bottomProfileClone);
    }

    const leftProfile = applyWorldMatrix(
      findObjectBy(frameModels[0], (object) => object?.name?.includes('profile_left_scaleable'))
    );

    const leftProfileBox = getModelBox([leftProfile]);
    const leftProfileSize = leftProfileBox.getSize(new Vector3());
    const heightDiff = profileSize.y - leftProfileSize.y;
    leftProfile.scale.y = (topPosition - heightDiff) / leftProfileSize.y;
    leftProfile.position.x = -(leftProfileBox.min.x + depthCorrection);
    const leftProfileBox2 = getModelBox([leftProfile]);
    leftProfile.position.y = -leftProfileBox2.min.y + leftProfileBox.min.y;

    const rightProfile = leftProfile.clone();
    rightProfile.scale.x = -1;
    rightProfile.position.x = length + leftProfileBox.min.x;

    const profileGroup = new Group();
    profileGroup.add(bottomProfileGroup, topProfile, rightProfile);
    withLeftProfile && profileGroup.add(leftProfile);
    bottomProfileCorner && profileGroup.add(bottomProfileCorner, topProfileCorner);
    sidePanels.length && profileGroup.add(...sidePanels);
    return profileGroup;
  }

  buildClosingHardWalls(height, lengthX, lengthZ, addSidePanel, configuration) {
    const wallModel = this.getClones('basemodel/wall')[0];
    if (!wallModel) {
      return;
    }

    // position correction prevents covering the side panel
    let sidePanelCorrection = 0;
    const doors = configuration.customdata.doors;
    const hasDoorAtFirstPosition = doors?.some(({ wall, position }) =>
      lengthZ ? wall === 'wall_2' && position === lengthZ - 1 : wall === 'wall_1' && position === 0
    );
    if (addSidePanel && hasDoorAtFirstPosition) {
      const frameModels = this.getModelsForComponent(
        /\/frame_color\/options\/frame_color_[^/]+\/frame_color_[^/]+$/
      );
      const sidePanel = findObjectBy(frameModels[0], (object) =>
        object?.name?.includes('sidepanel_scaleable')
      );
      if (sidePanel) {
        sidePanelCorrection = getBoxWidth(getModelBox(sidePanel)) * 0.5;
      }
    }

    const wallGroup = new Group();
    const wallBox = getModelBox([wallModel]);
    const wallSize = wallBox.getSize(new Vector3());
    const wallScaleY = height / wallSize.y;

    const wallRight = wallModel.clone();
    wallRight.scale.y = wallScaleY;
    wallRight.position.x = lengthX - wallBox.min.x;
    wallGroup.add(wallRight);

    const wallLeft = wallModel.clone();
    wallLeft.scale.y = wallScaleY;
    if (lengthZ) {
      wallLeft.rotation.y = 0.5 * Math.PI;
      wallLeft.position.x = wallBox.max.z;
      wallLeft.position.z = -lengthZ + wallBox.min.x - sidePanelCorrection;
    } else {
      wallLeft.position.x = -wallBox.max.x - sidePanelCorrection;
    }
    wallGroup.add(wallLeft);
    return wallGroup;
  }

  buildWallsFromModels(configuration) {
    if (!this.sceneContainer?.children?.length) {
      console.log('Scene is empty, skipping update of dynamic parts');
      return;
    }

    if (!this.buildContainer) {
      this.buildContainer = new Group();
      this.scene.add(this.buildContainer);
    }

    this.buildContainer.clear();

    // hide every part initially
    for (const child of this.sceneContainer.children) {
      child.visible = false;
    }

    const measurementsComponent = getComponent(configuration, 'measurements');
    const lengthX = getOptionInputValue(getOption(measurementsComponent, 'wall_length_first')) / 1000;
    const lengthZ = getOptionInputValue(getOption(measurementsComponent, 'wall_length_second')) / 1000;
    const height = getOptionInputValue(getOption(measurementsComponent, 'wall_height')) / 1000;

    const doorsX = configuration.customdata.doors?.filter(({ wall }) => wall === 'wall_1') || [];
    const doorsZ = configuration.customdata.doors?.filter(({ wall }) => wall === 'wall_2') || [];
    const frameHeight = getFrameHeight(configuration, height);
    const topWallHeight = height - frameHeight;

    const wallSegmentPositionsX = this.getWallSegmentPositions(doorsX, lengthX, topWallHeight, frameHeight);
    const wallSegmentPositionsZ = this.getWallSegmentPositions(doorsZ, lengthZ, topWallHeight, frameHeight);

    const wallLengths = {
      wall_1: lengthX,
      wall_2: lengthZ,
    };

    const wallDoors = {
      wall_1: doorsX,
      wall_2: doorsZ,
    };

    const isPanelEditable = ({ position, wall }) =>
      position + 1 <= wallLengths[wall] && (!lengthZ || position > 0);

    const isPanelHighlightEnabled = ({ wall, position }) => {
      return wallDoors[wall].length < 2 && !wallDoors[wall].some((door) => door.position === position);
    };

    const wallX = this.buildSingleWall(
      wallSegmentPositionsX,
      height,
      'wall_1',
      isPanelEditable,
      isPanelHighlightEnabled
    );

    const objects = [wallX];

    if (lengthZ) {
      const wallZ = this.buildSingleWall(
        wallSegmentPositionsZ,
        height,
        'wall_2',
        isPanelEditable,
        isPanelHighlightEnabled
      );
      wallZ.rotation.y = 0.5 * Math.PI;
      wallZ.scale.z = -1;
      objects.push(wallZ);
    }

    const addSidePanel = getSelectedOptions(getComponent(configuration, 'additional_parts')).some(
      ({ identifier }) => identifier === 'side_panel'
    );

    const hardWalls = this.buildClosingHardWalls(height, lengthX, lengthZ, addSidePanel, configuration);
    hardWalls && objects.push(hardWalls);

    const frameX = this.buildFrame(height, lengthX, wallSegmentPositionsX, addSidePanel, false, !lengthZ);
    frameX && objects.push(frameX);

    if (lengthZ) {
      const frameZ = this.buildFrame(height, lengthZ, wallSegmentPositionsZ, addSidePanel, true);
      frameZ.rotation.y = 0.5 * Math.PI;
      frameZ.scale.z = -1;
      objects.push(frameZ);
    }

    const doors = this.addDoors(configuration, { height, frameX });
    doors.length && objects.push(...doors);

    this.buildContainer.add(...objects);
    this.buildContainer.visible = true;

    const previousDefaultView = this.cameraViews.default;

    // force always recreate default screenshot view
    delete this.relativeScreenshotViews.default;

    this.fitSceneToCamera();
    this.initCameraViews(this.getCameras(this.sceneContainer));

    if (!areCameraViewsEqual(previousDefaultView, this.cameraViews.default)) {
      this.currentCameraView = null;
      this.jumpToView('default');
    }
  }

  showAddDoorHintIfNeeded(configuration) {
    const doorTypeOptionSelected = getSelectedOptions(getComponent(configuration, 'door_types'))[0];
    const noDoorAddedYet = !configuration.customdata.doors?.length;
    if (doorTypeOptionSelected && doorTypeOptionSelected?.identifier !== 'no_door' && noDoorAddedYet) {
      const firstEditable = findFirstEditable(this.buildContainer);
      if (firstEditable) {
        this.highlightObject(firstEditable);
        this.keepHighlighted = true;
      }
    } else if (this.keepHighlighted) {
      this.keepHighlighted = false;
      this.removeHighlight();
    }
  }

  async update(configuration) {
    if (configuration.item.identifier !== this._configuration?.item.identifier) {
      delete this.relativeCameraViews.default;
    }
    await super.update(configuration);
    this.buildWallsFromModels(configuration);
    this.showAddDoorHintIfNeeded(configuration);
    this.render();
  }

  addDoors(configuration, options = {}) {
    const doors = configuration.customdata.doors;
    const frameHeight = getFrameHeight(configuration, options.height);
    const doorTypeOption = getSelectedOptions(getComponent(configuration, 'door_types'))[0];
    const doorHasGlass = /^door_(glass|aluminium)/.test(doorTypeOption?.identifier);

    const door = this.getClones(/\/door_types(\+frame_color)?\//)[0];
    const doorFrame = this.getClones(/\/frame_color\/options\/frame_color_[^/]+\/door_frame_color_[^/]+$/)[0];
    const doorFittings = this.getClones('/door_types+door_fitting_color/')[0];
    const foil = doorHasGlass && this.getFoilModelWithCut(frameHeight);
    if (!doors || !door) {
      return [];
    }

    const referenceBox = this.getReferenceBox();
    const referenceHeight = getBoxHeight(referenceBox);

    const doorObject = new Group();

    const doorPanel = applyWorldMatrix(door, centerX);
    const doorPanelBox = getModelBox(door);
    const doorPanelHeight = getBoxHeight(doorPanelBox);
    const diff = referenceHeight - doorPanelHeight;
    const doorPanelTargetHeight = frameHeight - diff;
    const scalableDoorPanelRegEx = /^door_.*_scal[e]?able/;
    const scalableFrameRegEx = /^inner_frame_.*_scal[e]?able/;
    const fixedFrameTopRegEx = /^inner_frame_.*_top/;
    const doorPanelScaleable = findObjectBy(
      doorPanel,
      (object) => object?.name && scalableDoorPanelRegEx.test(object.name)
    );
    if (doorPanelScaleable) {
      const fixedFrameTop = applyWorldMatrix(
        findObjectBy(doorPanel, (object) => object?.name && fixedFrameTopRegEx.test(object.name)),
        makeTopOrigin
      );
      const upperPart = applyWorldMatrix(doorPanelScaleable, makeTopOrigin);
      const upperPartHeight = getModelHeight(upperPart);
      const fixedPartHeight =
        doorPanelHeight - upperPartHeight - (fixedFrameTop ? getModelHeight(fixedFrameTop) : 0);
      const upperPartScaleY = (doorPanelTargetHeight - fixedPartHeight) / upperPartHeight;
      upperPart.scale.y *= upperPartScaleY;
      upperPart.position.y = doorPanelTargetHeight;

      doorObject.add(upperPart);

      const frameScaleable = applyWorldMatrix(
        findObjectBy(doorPanel, (object) => object?.name && scalableFrameRegEx.test(object.name)),
        makeTopOrigin
      );
      if (frameScaleable) {
        frameScaleable.scale.y *= upperPartScaleY;
        frameScaleable.position.y = doorPanelTargetHeight;
        doorObject.add(frameScaleable);
      }

      if (fixedFrameTop) {
        fixedFrameTop.position.y = doorPanelTargetHeight;
        doorObject.add(fixedFrameTop);
      }
    } else {
      doorPanel.scale.y = doorPanelTargetHeight / referenceHeight;
      doorPanel.position.y = doorPanelBox.min.y;
    }

    doorObject.add(doorPanel);

    const doorLeafBox = getModelBox(doorObject);

    if (foil) {
      foil.position.z = doorPanelBox.getCenter(new Vector3()).z;
      const glassPart = findObjectBy(doorPanel, (object) => object?.name?.includes('glass'));
      if (glassPart) {
        foil.scale.x *= getBoxWidth(getModelBox(glassPart)) / getBoxWidth(getModelBox(foil));
      }
      doorObject.add(foil);
    }

    if (doorFrame) {
      centerX(doorFrame);

      const frameTop = applyWorldMatrix(
        findObjectBy(doorFrame, (object) => object?.name?.startsWith('frame_top')),
        makeTopOrigin
      );
      frameTop.position.y = frameHeight;
      const frameTopHeight = getModelHeight(frameTop);

      const frameLeft = applyWorldMatrix(
        findObjectBy(doorFrame, (object) => object?.name?.startsWith('frame_left'))
      );
      frameLeft.scale.y = (frameHeight - frameTopHeight) / getModelHeight(frameLeft);
      makeBottomOrigin(frameLeft);

      const frameRight = frameLeft.clone();
      frameRight.scale.x = -1;

      doorObject.add(frameTop, frameLeft, frameRight);
    }
    if (doorFittings) {
      const handle = applyWorldMatrix(
        findObjectBy(doorFittings, (object) => object?.name?.includes('_handle_'))
      );
      doorObject.add(handle);
      const hingeTop = applyWorldMatrix(
        findObjectBy(doorFittings, (object) => object?.name?.includes('_hinge_'))
      );
      hingeTop.position.y = doorLeafBox.max.y - 0.25;
      const hingeBottom = hingeTop.clone();
      hingeBottom.position.y = doorLeafBox.min.y + 0.375;
      doorObject.add(hingeTop, hingeBottom);
      if (frameHeight >= 2.4) {
        const hingeMiddle = hingeTop.clone();
        hingeMiddle.position.y -= 0.325;
        doorObject.add(hingeMiddle);
      }
    }

    return doors.map(({ wall, position }) => {
      const doorClone = doorObject.clone();

      Object.assign(doorClone.userData, { wall, position, editable: true });

      if (wall === 'wall_1') {
        doorClone.position.x = position + 0.5;
      } else if (wall === 'wall_2') {
        doorClone.rotation.y = -0.5 * Math.PI;
        doorClone.position.z = -position - 0.5;
      }
      return doorClone;
    });
  }
}
